import type { Map as GlobeMap, CustomLayerProjectionData } from 'maplibre-gl';
import type { Point } from '../types/app.js';

type CameraFrame = Omit<CustomLayerProjectionData, 'mainMatrix' | 'fallbackMatrix'> & { mainMatrix: number[]; fallbackMatrix: number[] };
const frames = new WeakMap<GlobeMap, CameraFrame>();
export function observeGlobeCamera(map: GlobeMap): void {
  map.addLayer({ id: 'globe-camera', type: 'custom', renderingMode: '2d', render(_gl, input) {
    const data = input.defaultProjectionData;
    frames.set(map, { ...data, mainMatrix: Array.from(data.mainMatrix), fallbackMatrix: Array.from(data.fallbackMatrix), clippingPlane: [...data.clippingPlane] });
  } });
}
export function globeCameraProjector(map: GlobeMap, width: number, height: number): (point: Point) => { x: number; y: number } {
  const data = frames.get(map);
  if (!data) throw new Error('The globe has not rendered yet.');
  return globeProjectorFromFrame(data, map.getCenter().lng, width, height);
}

export function globeProjectorFromFrame(data: CameraFrame | CustomLayerProjectionData, centerLng: number, width: number, height: number) {
  return (point: Point) => {
    const lng = point.lng * Math.PI / 180, lat = point.lat * Math.PI / 180;
    const sphere = [Math.sin(lng) * Math.cos(lat), Math.sin(lat), Math.cos(lng) * Math.cos(lat), 1];
    const multiply = (matrix: ArrayLike<number>, vector: number[]) => [0, 1, 2, 3].map(row => vector.reduce((sum, value, col) => sum + matrix[col * 4 + row] * value, 0));
    const globe = multiply(data.mainMatrix, sphere);
    const mercatorLng = centerLng + ((point.lng - centerLng + 180) % 360 + 360) % 360 - 180;
    const flat = multiply(data.fallbackMatrix, [(mercatorLng + 180) / 360, (1 - Math.log(Math.tan(Math.PI / 4 + Math.max(-89.999999, Math.min(89.999999, point.lat)) * Math.PI / 360)) / Math.PI) / 2, 0, 1]);
    const blend = data.projectionTransition;
    const clip = globe.map((value, i) => flat[i] * (1 - blend) + value * blend);
    const plane = sphere.reduce((sum, value, i) => sum + value * data.clippingPlane[i], 0);
    if (clip[3] <= 0 || (blend > 0.999 && plane < 0)) return { x: Infinity, y: Infinity };
    return { x: (clip[0] / clip[3] + 1) * width / 2, y: (1 - clip[1] / clip[3]) * height / 2 };
  };
}
